<template>
    <div class="w-100">
        <v-container fluid>
            <v-toolbar flat>
                <v-tabs v-model="tab" show-arrows>
                    <v-tab href="#map">Карта</v-tab>
                    <v-tab href="#wells">Графики скважин</v-tab>
                    <v-tab href="#cumulative">Накопленные графики</v-tab>
                    <v-tab href="#ofp">Графики ОФП</v-tab>
                </v-tabs>
            </v-toolbar>
        </v-container>
        <v-container fluid class="justify-space-between">
            <v-tabs-items v-model="tab" class="allow-overflow mb-8">
                <v-tab-item value="map" :transition="false">
                    <div class="panel-map">
                        <h3 class="panel-header" v-if="mapPanel.title" v-html="mapPanel.title"></h3>
                        <plotly-map v-if="item.reservoir && isWellsOptimize"
                                    :reservoir="item.reservoir"
                                    :wells="item.wells"
                                    :connections="connections"
                                    :grid-values="wellsGridValues"
                                    z-coord-prop="z_coord"
                                    :grids_y="item.reservoir.grids_z"
                                    :dy="item.reservoir.dz"
                                    type="opt"
                        ></plotly-map>
                        <plotly-map v-else-if="item.reservoir && getResult(mapPanel) && gridValues[mapPanel.id] && !loading"
                                    :reservoir="item.reservoir"
                                    :wells="item.wells"
                                    :connections="connections"
                                    :grid-values="gridValues[mapPanel.id]"
                                    z-coord-prop="z_coord"
                                    :grids_y="item.reservoir.grids_z"
                                    :dy="item.reservoir.dz"
                                    :panel="mapPanel"
                                    :result="getResult(mapPanel)"
                                    type="map"
                        ></plotly-map>
                        <div class="panel-extra">
                            <v-slider v-if="timeScale"
                                      v-model="sliderTime[mapPanel.id]"
                                      min="0"
                                      :max="timeScale.length"
                                      thumb-label
                                      ticks
                                      hide-details
                            >
                                <template #prepend>{{timeScale[0]}}</template>
                                <template #thumb-label="{value}">{{timeScale[value]}}</template>
                                <template #append>{{timeScale[timeScale.length-1]}}</template>
                            </v-slider>
                            <v-btn class="ml-4" elevation="0" color="primary" @click="applyPanelTime(mapPanel.id)" :disabled="isTimeApplied(mapPanel.id)">Применить</v-btn>
                        </div>
                    </div>
                </v-tab-item>
                <v-tab-item value="wells" :transition="false">
                    <h3 class="panel-header" v-if="wellsPanel.title" v-html="wellsPanel.title"></h3>
                    <plotly :data="getPlotData(item, wellsPanel)" :layout="getPlotLayout(item, wellsPanel)"></plotly>
                </v-tab-item>
                <v-tab-item value="cumulative" :transition="false">
                    <h3 class="panel-header" v-if="cumulativePanel.title" v-html="cumulativePanel.title"></h3>
                    <plotly :data="getPlotData(item, cumulativePanel, true)" :layout="getPlotLayout(item, cumulativePanel)"></plotly>
                </v-tab-item>
                <v-tab-item value="ofp" :transition="false">
                    <h3 class="panel-header" v-if="ofpPanel.title" v-html="ofpPanel.title"></h3>
                    <plotly :data="getOfpPlots(item, ofpPanel)" :layout="getPlotLayout(item, ofpPanel)"></plotly>
                </v-tab-item>
            </v-tabs-items>
            <v-navigation-drawer
                permanent
                right
                height="100vh"
            >
                <v-list flat subheader>
                    <v-list-item-group :value="graphWellsIndexes"
                                  multiple
                                  active-class=""
                                  @change="setCurrentGraphWells"
                    >
                        <v-list-item v-for="well in allWells" :key="well.value">
                            <template v-slot:default="{ active }">
                                <v-list-item-action>
                                    <v-checkbox :input-value="active"></v-checkbox>
                                </v-list-item-action>

                                <v-list-item-content>
                                    <v-list-item-title>{{well.text}}</v-list-item-title>
                                </v-list-item-content>
                            </template>
                        </v-list-item>
                    </v-list-item-group>
                </v-list>
            </v-navigation-drawer>
        </v-container>
    </div>
</template>

<script>
import PlotlyMap from "@/components/Problem/PlotlyMap.vue";
import { Plotly } from 'vue-plotly';
import clone from "lodash.clonedeep";
import {debounce} from "lodash";
import axios from "axios";
import {bilinear} from "@/interpolate";

export default {
    name: "AdaptResult",
    props: ['item', 'task', 'optimizeTask'],
    components: {
        Plotly, PlotlyMap
    },
    data() {
        return {
            totalCols: 2,
            panelsInited: false,
            mapPanel: {id: 'map', type: 'map', param: 'Sw'},
            wellsPanel: {id: 'wells', type: 'wells', param: 'debit'},
            cumulativePanel: {id: 'cumulative', type: 'cumulative', param: 'total_debit'},
            ofpPanel: {id: 'ofp', type: 'ofp'},

            tab: 'map',
            panelMenus: {},
            panelTime: {},
            sliderTime: {},
            graphWellsIndexes: [],
            types: [
                {code: 'map', title: 'Карта', children: [
                        {code: 'map', param: 'Sw', title: 'S<sub>w</sub>'},
                        {code: 'map', param: 'So', title: 'S<sub>o</sub>'},
                        {code: 'map', param: 'pressure', title: 'P'},
                        {code: 'map', param: 'debit', title: 'Дебит'},
                        {code: 'map', param: 'oil', title: 'Нефть'},
                        {code: 'map', param: 'ff', title: 'F'},
                ]},
                {code: 'plot', title: 'Графики скважин', children: [
                        {code: 'plot', param: 'Sw', title: 'S<sub>w</sub>'},
                        {code: 'plot', param: 'pressure', title: 'P'},
                        {code: 'plot', param: 'debit', title: 'Дебит'},
                        {code: 'plot', param: 'oil', title: 'Нефть'},
                        {code: 'plot', param: 'ff', title: 'F'},
                ]},
                {code: 'cumulative', title: 'Накопленные графики', children: [
                        {code: 'cumulative', param: 'debit', title: 'Накопленный дебит по скважинам'},
                        {code: 'cumulative', param: 'oil', title: 'Накопленная нефть по скважинам'},
                        {code: 'cumulative', param: 'total_debit', title: 'Суммарный дебит'},
                        {code: 'cumulative', param: 'total_oil', title: 'Суммарная нефть'},
                ]},
                {code: 'ofp', title: 'Графики ОФП', children: []},
            ],
            typesHash: 0,
            throttledSave: null,
            throttledValuesUpdate: null,
            gridValues: {},
            wellsGridValues: [],
            loadedInterpolate: {},
            keys: {},
            allWellResults: {},
            connections: [],
            loadingRequests: {},
            loading: false,
        }
    },
    watch: {
        panels: {
            deep: true,
            async handler() {
                //await this.throttledSave();
                await this.throttledValuesUpdate();
                await this.loadAllPanelResults();
                //await this.updateConnections();
            },
        },
        task: {
            deep: true,
            async handler() {
                if (!this.task) {
                    this.panelsInited = false;
                    return;
                }

                await this.loadAllPanelResults();
                this.updateGridValues();
                this.panelsInited = true;
            }
        },
        item: {
            deep: true,
            handler() {
                //this.populateOfpChildren();
            }
        },
        resultConnections() {
            //this.updateConnections();
        },
        connections() {
            //this.populateOfpChildren();
        },
        result() {
            this.updateGridValues();
            //this.populateOfpChildren();
        }
    },
    created() {
        this.throttledSave = debounce(this.savePanels.bind(this), 1000);
        // this.throttledValuesUpdate = debounce(this.updateGridValues.bind(this), 1000);
        this.throttledValuesUpdate = this.updateGridValues.bind(this);

        this.updateGridValues();
        //this.populateOfpChildren();
    },
    methods: {
        applyPanelTime(panelId) {
            this.$set(this.panelTime, panelId, this.sliderTime[panelId]);
            let panel = this.getPanelById(panelId);
            if (panel) {
                this.$set(panel, 'timeIndex', this.sliderTime[panelId]);
            }
            this.updateGridValues();
        },
        isTimeApplied(panelId) {
            return this.sliderTime[panelId] === this.panelTime[panelId];
        },
        getPanelById(panelId) {
            if (!this.panels) {
                return null;
            }

            for (let panel of this.panels) {
                if (panel.id === panelId) {
                    return panel;
                }
            }

            return null;
        },
        addPanel(type) {
        },
        removePanel(panel, type) {
        },
        canAdd(type) {
            return false;
        },
        async makeGetRequest(url) {
            let promise;
            if (this.loadingRequests[url]) {
                promise = this.loadingRequests[url];
            }
            else {
                promise = axios.get(url);
                this.loadingRequests[url] = promise;
            }

            let result = await promise;
            this.$set(this.loadingRequests, url, null);

            this.loading = Object.keys(this.loadingRequests).reduce((anyLoading, url) => {
                anyLoading = anyLoading || this.loadingRequests[url] !== null;
                return anyLoading;
            }, false);

            return result;
        },
        changeType(panel, type) {
            panel.type = type.code;
            panel.param = type.param || null;
            panel.title = type.title || null;
            this.panelMenus[panel.id] = false;

            if (this.isWellsPredict) {
                let selectedWellIndex = this.task?.max_well_index;
                if (typeof(selectedWellIndex) !== 'undefined') {
                    let well = this.item.wells[selectedWellIndex];
                    if (well) {
                        if (panel.type === 'map') {
                            panel.graphWells = well.id;
                        } else {
                            panel.graphWells = [-1, well.id];
                        }
                    }
                }
            }
        },
        async getGridValues(wellAttr, item, task, t = 0, panel) {
            let isSo = wellAttr === 'So';
            if (!task) {
                return false;
            }

            if (isSo) {
                wellAttr = 'Sw';
            }

            let loadedResults;
            try {
                loadedResults = this.loadedInterpolate[task?.id][wellAttr][t];
            }
            catch (e) {
                loadedResults = false;
            }

            if (!loadedResults) {
                let predictWell = false;
                if (panel && panel.graphWells) {
                    if (panel.graphWells instanceof Array) {
                        predictWell = panel.graphWells.find(item => item !== -1);
                    }
                    else {
                        predictWell = panel.graphWells;
                    }
                }

                if (predictWell === -1) {
                    predictWell = false;
                }

                if (this.isWellsPredict && predictWell) {
                    let interpolateUrl = this.isWellsPredict && predictWell
                        ? '/storage/interpolate/' + task?.id + '/' + predictWell + '_' + wellAttr + '.json'
                        : '/storage/interpolate/' + task?.id + '/' + wellAttr + '.json';

                    try {
                        let response = await this.makeGetRequest(interpolateUrl);

                        if (response.data) {
                            if (typeof (this.loadedInterpolate[task?.id]) === 'undefined') {
                                this.loadedInterpolate[task?.id] = {};
                            }

                            this.loadedInterpolate[task?.id][wellAttr] = response.data;
                            loadedResults = response.data[t];
                        }
                    } catch (e) {
                        loadedResults = false;
                    }
                }
                else {
                    let timestepResults = await this.interpolateValuesBackend({
                        task_id: task?.id,
                        param: wellAttr,
                        time_step: this.timeScale[t]
                    });

                    if (typeof (this.loadedInterpolate[task?.id]) === 'undefined') {
                        this.loadedInterpolate[task?.id] = {}
                    }

                    if (typeof (this.loadedInterpolate[task?.id][wellAttr]) === 'undefined') {
                        this.loadedInterpolate[task?.id][wellAttr] = {}
                    }

                    this.loadedInterpolate[task?.id][wellAttr][t] = timestepResults;
                    loadedResults = timestepResults;
                }
            }

            if (isSo && loadedResults) {
                loadedResults = loadedResults.map(row => {
                    return row.map(Sw => 1-Sw);
                });
            }

            return loadedResults;
        },
        updateGridValues() {
            if (!this.task) {
                return;
            }

            if (!this.result) {
                return;
            }

            if (this.taskType === 'wells') {
                let timeIndex = (this.timeScale?.length || 1) - 1;

                this.getGridValues('Sw', this.item, this.task, timeIndex)
                    .then(values => {
                        this.wellsGridValues = values;
                    });
            }

            for (let panel of this.panels) {
                if (panel.type === 'map') {
                    this.getGridValues(panel.param, this.item, this.task, this.panelTime[panel.id]||0, panel)
                        .then(values => {
                            this.$set(this.gridValues, panel.id, values);
                            this.$set(this.keys, panel.id, this.keys[panel.id] ? this.keys[panel.id] + 1 : 1);
                        });
                }
            }
        },
        async interpolateValuesBackend(data) {
            try {
                let response = await axios.post('/api/calc/interpolate', data);

                if (response.data.interpolated) {
                    return response.data.interpolated;
                }
            }
            catch (e) {}

            return data;
        },
        async interpolateValues(data) {
            return bilinear(data);
        },
        krw(x, a, nw) {
            let siw = this.item.s_iw_irreducible_water_saturation;
            let sor = this.item.s_or_residual_oil_saturation;
            return a * ((x-siw)/(1-siw-sor))**nw;
        },
        kro(x, no) {
            let siw = this.item.s_iw_irreducible_water_saturation;
            let sor = this.item.s_or_residual_oil_saturation;
            return ((1-x-sor)/(1-siw-sor))**no;
        },
        getOfpPlots(item, panel) {
            if (!this.result?.model) {
                return [];
            }

            let a, no, nw;
            if (panel.param === 'average') {
                a = this.result.model['a'];
                no = this.result.model['no'];
                nw = this.result.model['nw'];
            }
            else {
                let connectionIndex = parseInt(panel.param.replace('connection_'));
                let connection = this.connections[connectionIndex];
                let i = connection[0].index;
                let j = connection[1].index;
                let paramSuffix = '['+i+']['+j+']';
                a = this.result.model['a'+paramSuffix];
                no = this.result.model['no'+paramSuffix];
                nw = this.result.model['nw'+paramSuffix];
            }

            if (a && no && nw) {
                let steps = 101;
                let sw = Array(steps).fill(0).map((_, i) => i/steps);
                let kro = sw.map(x => this.kro(x, no));
                let krw = sw.map(x => this.krw(x, a, nw));

                return [
                    {
                        x: sw,
                        y: kro,
                        mode: 'lines',
                        type: 'scatter',
                        name: 'Kro',
                    },
                    {
                        x: sw,
                        y: krw,
                        mode: 'lines',
                        type: 'scatter',
                        name: 'Krw'
                    }
                ]
            }

            return [];
        },
        getPlotData(item, panel, isCumulative = false) {
            if (!item || !panel || !this.result) {
                return [];
            }

            let selectedWells = panel.wellIds || null;
            let wells = item.wells || [];
            let wellIndexes = wells
                .map((well, index) => index)
                .filter(wellIndex => {
                    let well = wells[wellIndex];
                    return selectedWells === null || selectedWells.includes(well.id);
                });

            let param = panel.param;
            let isTotal = param.indexOf('total_') !== -1;
            if (isTotal) {
                param = param.replace('total_', '');
            }

            let results = this.getResult(panel);
            if (!(results instanceof Array)) {
                results = [results];
            }

            let nodesYs = results.map(result => {
                let nodes = result.nodes.filter(node => wellIndexes.includes(node.well));

                return nodes.map((node, nodeIndex) => {
                    let nodeData = result.nodes[nodeIndex];
                    let nodeY = nodeData ? nodeData[param] || [] : [];
                    if (isCumulative) {
                        let cumulativeWellY = [];
                        let totalY = 0;
                        for (let y of nodeY) {
                            totalY += y;
                            cumulativeWellY.push(totalY);
                        }
                        nodeY = cumulativeWellY;
                    }
                    return nodeY;
                });
            });

            if (isTotal) {
                return nodesYs.map((nodesY, graphIndex) => {
                    let totalYs = [];
                    for (let nodeY of nodesY) {
                        for (let pointIndex in nodeY) {
                            let currentY = nodeY[pointIndex];
                            let totalY = totalYs[pointIndex] || 0;
                            totalYs[pointIndex] = totalY + currentY;
                        }
                    }

                    return {
                        x: results[graphIndex].time_days,
                        y: totalYs,
                        mode: 'lines',
                        type: 'scatter',
                        name: panel.title
                    };
                });
            }

            let graphs = nodesYs.map((nodesY, graphIndex) => nodesY.map((nodeY, nodeIndex) => {
                let result = results[graphIndex];
                let nodes = result.nodes.filter(node => wellIndexes.includes(node.well));
                let node = nodes[nodeIndex];
                let wellIndex = node['well'];
                let well = wells[wellIndex];
                let openWellIndex = result['well_index'];
                let openWellId = false;
                if (openWellIndex) {
                    openWellId = openWellIndex === -1
                        ? 'Без новой скважины'
                        : wells[openWellIndex].id
                }

                return {
                    x: results[graphIndex].time_days,
                    y: nodeY,
                    mode: 'lines',
                    type: 'scatter',
                    name: openWellId
                        ? well.id + '(' +openWellId+')'
                        : well.id,
                }
            }));

            graphs = graphs.reduce((resultGraphs, wellGraphs) => {
                return resultGraphs.concat(wellGraphs);
            }, []);

            return graphs;
        },
        getPlotLayout(item, panel) {
            let layout = {
                plot_bgcolor: '#d9d9d9',
                paper_bgcolor: '#d9d9d9',
                margin: {
                    pad: 16
                }
            };

            if (panel.type === 'ofp') {
                let siw = this.item.s_iw_irreducible_water_saturation;
                let sor = this.item.s_or_residual_oil_saturation;

                layout['xaxis'] = {
                    title: 'Sw',
                    range: [siw, 1-sor],
                };

                layout['yaxis'] = {
                    range: [0, 1]
                }
            }

            if (this.isPredict && this.historyEndTime) {
                layout['shapes'] = [{
                    type: 'line',
                    x0: this.historyEndTime,
                    y0: 0,
                    x1: this.historyEndTime,
                    yref: 'paper',
                    y1: 1,
                    line: {
                        color: 'red',
                        width: 1.5,
                        dash: 'dot',
                    }
                }];
            }

            return layout;
        },
        async savePanels() {
            if (!this.task) {
                return;
            }

            this.task.panels = clone(this.panels);
            //await this.$store.dispatch('task/saveItem', {item: this.task, silent: true});
        },
        async loadAllPanelResults() {
            if (this.panelsInited) {
                for (let panel of this.panels) {
                    await this.loadResult(panel);
                }
            }
        },
        async loadResult(panel = false) {
            let wellIds = panel.graphWells || [];
            if (!wellIds) {
                return;
            }

            if (!(wellIds instanceof Array)) {
                wellIds = [wellIds];
            }

            let loadedWellIds = Object.keys(this.allWellResults);
            let newWellIds = wellIds.filter(wellId => !loadedWellIds.includes(wellId) && wellId !== -1);
            for (let wellId of newWellIds) {
                let predictUrl = '/storage/predicts/'+this.task?.id+'/'+wellId+'.json';
                try {
                    let response = await this.makeGetRequest(predictUrl);
                    this.allWellResults[wellId] = response.data;
                }
                catch (e) {
                    this.allWellResults[wellId] = null;
                }
            }
        },
        getResult(panel = false) {
            if (!this.taskType) {
                return;
            }

            if (!this.item || (this.item && !this.item.wells)){
                return;
            }

            if (panel && this.taskType === 'predict' && this.task?.sub_type === 'wells') {
                let wellIds = panel.graphWells || [];
                if (!(wellIds instanceof Array)) {
                    wellIds = [wellIds];
                }

                let wellIndexes = this.item.wells
                    .map((well, index) => [well.id, index])
                    .filter(wellIdIndex => wellIds.includes(wellIdIndex[0]))
                    .map(wellIdIndex => wellIdIndex[1]);

                let wellResults = this.task['well_predicts'] || Array(this.item.wells.length).fill(null);
                for (let wellIndex in this.item.wells) {
                    let well = this.item.wells[wellIndex];
                    if (well) {
                        let wellData = this.allWellResults[well.id];
                        if (wellData && !wellResults[wellIndex]) {
                            wellResults[wellIndex] = wellData;
                        }
                    }
                }

                if (wellResults instanceof Array) {
                    wellResults = wellResults.filter((item, index) => {
                        return item !== null && wellIndexes.includes(index);
                    });
                }

                if (wellIds.includes(-1)) {
                    wellResults.unshift(this.task['predict']);
                }

                return wellResults;
            }

            let resultKey = this.taskType === 'wells'
                ? 'optimize'
                : this.taskType;

            return this.task[resultKey] || null;
        },
        populateOfpChildren() {
            if (!this.item) {
                return;
            }

            if (!this.result) {
                return;
            }

            let ofpIndex = this.types.findIndex(typeItem => typeItem.code === 'ofp');
            if (ofpIndex === -1) {
                return;
            }

            let ofp = this.types[ofpIndex];
            let isAverage = typeof(this.result.model['a']) !== 'undefined';
            if (isAverage) {
                ofp.children = [{code: 'ofp', param: 'average', title: 'ОФП для месторождения'}];
            }
            else {
                ofp.children = this.connections.map((connection, index) => {
                    let title = 'Переток ' + connection[0].id + '-' + connection[1].id;
                    return {code: 'ofp', param: 'connection_' + index, title: title};
                });
            }

            this.typesHash++;
        },
        updateConnections() {
            let result = this.getResult();

            if (!this.item || !this.item.wells) {
                return [];
            }

            if (this.item.connections) {
                return this.item.connections;
            }

            if (!result) {
                return [];
            }

            if (!result.connections) {
                return [];
            }

            let connections = [];
            let wellConnections = result.connections.split(',');
            for (let firstWellIndex = 0; firstWellIndex < wellConnections.length; firstWellIndex++) {
                let connectedTo = wellConnections[firstWellIndex].split('');
                for (let secondWellIndex = 0; secondWellIndex < connectedTo.length; secondWellIndex++) {
                    let isConnected = connectedTo[secondWellIndex] === '1';
                    if (isConnected) {
                        let firstWell = this.item.wells[firstWellIndex];
                        let secondWell = this.item.wells[secondWellIndex];
                        if (firstWell && secondWell) {
                            let paramIndex = '['+firstWellIndex+']['+secondWellIndex+']';
                            connections.push([
                                {x: firstWell.x_coord, y: firstWell.z_coord, id: firstWell.id, index: firstWellIndex},
                                {x: secondWell.x_coord, y: secondWell.z_coord, id: secondWell.id, index: secondWellIndex},
                                {T: result?.model['T'+paramIndex]}
                            ])
                        }
                    }
                }
            }

            this.connections = connections;
            return connections;
        },
        setCurrentGraphWells(newGraphWellIndexes) {
            this.graphWellsIndexes = newGraphWellIndexes;
            this.currentPanel.graphWells = newGraphWellIndexes.map(index => this.allWells[index].value);
        }
    },
    computed: {
        taskType() {
            return this.task?.type || null;
        },
        isPredict() {
            return this.taskType === 'predict';
        },
        isWellsPredict() {
            return this.task?.type === 'predict' && this.task?.sub_type === 'wells';
        },
        isOptimize() {
            return this.taskType === 'optimize';
        },
        isWellsOptimize() {
            return this.taskType === 'wells';
        },
        result() {
            return this.getResult();
        },
        resultConnections() {
            return this.result?.connections || null;
        },
        timeScale() {
            let result = this.isOptimize || this.isWellsOptimize
                ? this.task['optimize']
                : this.task['predict'];

            return result ? result['time_days'] || [] : [];
        },
        historyEndTime() {
            let historySteps = this.optimizeTask?.optimize?.time_days || [];
            return historySteps.length > 0 ? historySteps[historySteps.length-1] : false;
        },
        allWells() {
          return this.item?.wells?.map(well => {
              return {
                  text: well.id,
                  value: well.id,
              }
          }) ;
        },
        closedWells() {
            let wellItems = this.task?.closed_wells_indexes
                .filter(wellIndex => Boolean(this.item?.wells && this.item.wells[wellIndex]))
                .map(wellIndex => {
                    let well = this.item.wells[wellIndex];
                    return {
                        text: well.id,
                        value: well.id
                    }
                }) || [];

            wellItems.unshift({
                text: 'Без новой скважины',
                value: -1,
            });

            return wellItems;
        },
        panels() {
            return [this.mapPanel, this.wellsPanel, this.cumulativePanel, this.ofpPanel];
        },
        typesWithOfp() {
            if (!this.item) {
                return this.types;
            }

            if (!this.result) {
                return this.types;
            }

            if (!this.result.model) {
                return this.types;
            }

            let ofpIndex = this.types.findIndex(typeItem => typeItem.code === 'ofp');
            if (ofpIndex === -1) {
                return this.types;
            }

            let ofp = this.types[ofpIndex];
            let isAverage = typeof(this.result.model['a']) !== 'undefined';
            if (isAverage) {
                ofp.children = [{code: 'ofp', param: 'average', title: 'ОФП для месторождения'}];
            }
            else {
                ofp.children = this.connections.map((connection, index) => {
                    let title = 'Переток ' + connection[0].id + '-' + connection[1].id;
                    return {code: 'ofp', param: 'connection_' + index, title: title};
                });
            }

            return this.types;
        },
        currentPanel() {
            let panelProp = this.tab + 'Panel';
            return this[panelProp];
        }
    }
}
</script>

<style lang="scss">
    .loading {
      opacity: 0.7;
    }

    .panel {
        background: #D9D9D9;
        position: relative;
        padding: 2em;

        .header-buttons {
            position: absolute;
            top: 1em;
            right: 1em;
            display: flex;
            flex-wrap: wrap;
        }
    }

    .grid {
        display: grid;
        gap: 1em;
        grid-auto-rows: minmax(300px, max-content);

        &__big {
            grid-template-columns: 100%;
        }

        &__medium {
            grid-template-columns: repeat(2, 1fr);
        }

        &__small {
            grid-template-columns: repeat(3, 1fr);
        }

        .buttons {
            display: flex;
            justify-content: center;
            align-items: center;
            border: 1px solid #D9D9D9;
        }
    }

    .panel {
        position: relative;

        &-extra {
            position: absolute;
            background: white;
            border-radius: 0.25em;
            width: 50%;
            left: 2em;
            top: 2em;
            padding: 1em;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        &-map {
            position: relative;
            &:before{
                content: "";
                display: block;
                padding-top: 100%;
            }

            .js-plotly-plot {
                position: absolute!important;
                top: 0;
                bottom: 0;
                left: 0;
                right: 0;
            }
        }
    }
</style>